import { getSurveillanceProgress } from "api/surveillance";

/**
 * Maps backend SurveillanceStep enum values to frontend `{ section, slug }`.
 * Used when navigating from the listing to the most recently completed step
 * for a surveillance lead.
 */
const WORKFLOW_STEP_TO_PATH: Record<
  string,
  { section: "discussion" | "status"; slug: string }
> = {
  SV_S1_01_PRELIMINARY_DISCUSSION: {
    section: "discussion",
    slug: "preliminary-discussion-with-the-client",
  },
  SV_S1_02_APPLICATION_DETAILS_FROM_CLIENT: {
    section: "discussion",
    slug: "application-details-from-client",
  },
  SV_S1_03_INVOICE_TO_CLIENT: {
    section: "discussion",
    slug: "invoice-to-client",
  },
  SV_S1_04_PAYMENT_FROM_CLIENT: {
    section: "discussion",
    slug: "payment-from-client",
  },
  SV_S3_01_DETAILED_SEARCH: {
    section: "status",
    slug: "detailed-search",
  },
  SV_S3_02_NOTE_OF_SURVEILLANCE: {
    section: "status",
    slug: "note-of-surveillance",
  },
  SV_S3_02A_FIELDS_SAVED: {
    section: "status",
    slug: "note-of-surveillance",
  },
  SV_S3_02B_PDF_GENERATED: {
    section: "status",
    slug: "note-of-surveillance",
  },
  SV_S3_02C_SENT_TO_CLIENT: {
    section: "status",
    slug: "note-of-surveillance",
  },
};

const DEFAULT_STEP = {
  section: "discussion" as const,
  slug: "preliminary-discussion-with-the-client",
};

/**
 * Resolves the in-app path for a surveillance lead's most recently completed
 * workflow step so the user lands directly on it from pending items.
 *
 * Falls back to the preliminary discussion step when the lead has no completed
 * steps yet, when the step has no implemented UI route, or when progress fails.
 */
export async function getSurveillancePathForLead(
  leadId: string
): Promise<string> {
  try {
    const progress = await getSurveillanceProgress(leadId);
    const lastCompleted = [...progress.steps]
      .reverse()
      .find((s) => s.isCompleted)?.slug;
    const stepKey =
      lastCompleted ?? progress.maxStepReached ?? progress.currentStep ?? null;
    const pathSpec = stepKey ? WORKFLOW_STEP_TO_PATH[stepKey] : null;
    const { section, slug } = pathSpec ?? DEFAULT_STEP;
    return `/dashboard/surveillance/new/${section}/${slug}/${leadId}`;
  } catch {
    return `/dashboard/surveillance/new/discussion/preliminary-discussion-with-the-client/${leadId}`;
  }
}
